import React, { useState, useRef, useEffect } from 'react';

/**
 * FeedbackPanel component for displaying and managing feedback for subquestions
 */
const FeedbackPanel = ({
  subQuestion,
  getFeedback,
  handleRemoveFeedback,
  handleEditFeedback,
  handleFeedbackChange,
  handleFeedbackSave,
  handleFeedbackCancel,
  disabled = false
}) => {
  // Helper function to extract text from feedback object or return empty string
  const extractFeedbackText = feedback => {
    if (!feedback) {
      return '';
    }
    if (typeof feedback === 'string') {
      return feedback;
    }
    if (typeof feedback === 'object') {
      // Look for common text properties in the feedback object
      return feedback.text || feedback.feedback || feedback.content || '';
    }
    return '';
  };

  // Local state to track if we're editing
  const [isEditing, setIsEditing] = useState(false);
  const [localFeedback, setLocalFeedback] = useState(
    extractFeedbackText(getFeedback(subQuestion.id))
  );

  // Update local feedback when the component mounts or the feedback changes
  // BUT only when not currently editing to prevent wiping out changes
  useEffect(() => {
    // Skip the update if the user is currently editing the feedback
    if (isEditing) {
      return;
    }

    const currentFeedback = extractFeedbackText(getFeedback(subQuestion.id));
    // console.log('useEffect detected feedback change:', currentFeedback);
    setLocalFeedback(currentFeedback);
  }, [getFeedback, subQuestion.id, isEditing]);
  const feedbackTextareaRef = useRef(null);

  // Helper function to render feedback content
  const renderFeedback = feedback => (
    <div className="mb-2 whitespace-pre-wrap">
      {feedback}
    </div>
  );

  // Function to insert symbol at cursor position
  const insertSymbolAtCursor = symbol => {
    if (!feedbackTextareaRef.current) {
      return;
    }

    const textarea = feedbackTextareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;

    // Insert the symbol at the cursor position
    const newText = text.substring(0, start) + symbol + text.substring(end);

    // Update the local feedback
    setLocalFeedback(newText);

    // Focus back on the textarea and set cursor position after the inserted symbol
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + symbol.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);

      // Auto-resize the textarea
      textarea.style.height = '0';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }, 0);
  };

  // Local handlers
  const handleEdit = () => {
    // Get current feedback text
    const currentFeedback = localFeedback || extractFeedbackText(getFeedback(subQuestion.id)) || '';

    // Update the local state with the current frontend state
    setLocalFeedback(currentFeedback);

    // Set editing mode after a very short delay to ensure React has updated the localFeedback
    // This helps prevent the empty textarea flicker
    setTimeout(() => {
      setIsEditing(true);

      // Focus the textarea and position cursor at the end after rendering is complete
      setTimeout(() => {
        if (feedbackTextareaRef.current) {
          // Set initial height before focus to prevent jumping
          feedbackTextareaRef.current.style.height = 'auto';
          feedbackTextareaRef.current.style.height = `${feedbackTextareaRef.current.scrollHeight}px`;

          feedbackTextareaRef.current.focus();

          // Position cursor at the end of the text
          const length = feedbackTextareaRef.current.value.length;
          feedbackTextareaRef.current.setSelectionRange(length, length);
        }
      }, 50);
    }, 10);
  };

  const handleChange = e => {
    // console.log('Text changed:', e.target.value);
    setLocalFeedback(e.target.value);
  };

  const handleSave = () => {
    // console.log('Save clicked with value:', localFeedback);
    // console.log('Saving feedback for subquestion ID:', subQuestion.id);

    // First update local state immediately for instant feedback
    setIsEditing(false);

    // Call API to save to backend - the parent component expects the text format, not an object
    // The parent component is responsible for creating the proper feedback object structure
    handleFeedbackSave(subQuestion.id, localFeedback);
  };

  const handleCancel = () => {
    // console.log('Cancel clicked');
    setIsEditing(false);
  };

  // console.log('FeedbackPanel rendering for subquestion:', subQuestion.id, 'isEditing:', isEditing, 'feedback:', getFeedback(subQuestion.id));

  return (
    <div
      style={{
        marginBottom: '0.5rem',
        padding: '0.5rem',
        backgroundColor: '#fff',
        borderRadius: '0.75rem',
        border: '1px solid #e4b17a',
        boxShadow: '0 2px 5px rgba(0, 0, 0, 0.05)',
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '0.25rem'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            color: '#e4b17a',
            fontWeight: '600',
            fontSize: '0.875rem'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            style={{
              height: '1.25rem',
              width: '1.25rem',
              marginRight: '0.5rem',
              color: '#e4b17a'
            }}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            />
          </svg>
          Feedback
        </div>
        {!isEditing && (
          <div>
            <button
              style={{
                color: disabled ? '#9ca3af' : '#4b5563',
                transition: 'color 0.2s',
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
              title={disabled ? "Disabled during AI learning" : "Remove feedback"}
              onClick={disabled ? undefined : () => handleRemoveFeedback(subQuestion.id)}
              disabled={disabled}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                style={{ height: '1.25rem', width: '1.25rem' }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div style={{ position: 'relative' }}>
          <textarea
            ref={feedbackTextareaRef}
            value={localFeedback}
            disabled={disabled}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid rgba(228,177,121,0.4)',
              borderRadius: '0.375rem',
              marginBottom: '0.5rem',
              minHeight: '80px',
              height: 'auto',
              resize: 'none',
              overflowY: 'hidden',
              backgroundColor: disabled ? '#f5f5f5' : '#fcfcfc',
              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              cursor: disabled ? 'not-allowed' : 'text',
              opacity: disabled ? 0.6 : 1
            }}
            rows={3} // Start with a reasonable number of rows
            placeholder={disabled ? "Editing disabled during AI learning" : "Add or edit feedback here..."}
            onChange={disabled ? undefined : (e) => {
              handleChange(e);
              // Auto-resize the textarea to fit all content
              e.target.style.height = '0'; // Reset height to recalculate
              e.target.style.height = `${e.target.scrollHeight}px`; // Set height to scrollHeight
            }}
          />

          {/* Symbol insertion buttons - positioned absolutely */}
          <div style={{
            position: 'absolute',
            top: '0.4rem',
            right: '0.5rem',
            display: 'flex',
            gap: '0.25rem',
            zIndex: 5
          }}>
            <button
              type="button"
              title={disabled ? "Disabled during AI learning" : "Insert check symbol (✓)"}
              disabled={disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                backgroundColor: disabled ? '#f5f5f5' : 'rgba(249, 250, 251, 0.95)',
                color: disabled ? '#9ca3af' : '#22c55e',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                opacity: disabled ? 0.5 : 1
              }}
              onMouseOver={disabled ? undefined : (e) => {
                e.currentTarget.style.backgroundColor = 'rgba(240, 253, 244, 0.95)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={disabled ? undefined : (e) => {
                e.currentTarget.style.backgroundColor = 'rgba(249, 250, 251, 0.95)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
              }}
              onClick={disabled ? undefined : () => insertSymbolAtCursor('✓ ')}
            >
              ✓
            </button>
            <button
              type="button"
              title={disabled ? "Disabled during AI learning" : "Insert cross symbol (✗)"}
              disabled={disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '1.5rem',
                height: '1.5rem',
                borderRadius: '0.375rem',
                border: '1px solid #e5e7eb',
                backgroundColor: disabled ? '#f5f5f5' : 'rgba(249, 250, 251, 0.95)',
                color: disabled ? '#9ca3af' : '#ef4444',
                fontSize: '0.85rem',
                fontWeight: 'bold',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                opacity: disabled ? 0.5 : 1
              }}
              onMouseOver={disabled ? undefined : (e) => {
                e.currentTarget.style.backgroundColor = 'rgba(254, 242, 242, 0.95)';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
              }}
              onMouseOut={disabled ? undefined : (e) => {
                e.currentTarget.style.backgroundColor = 'rgba(249, 250, 251, 0.95)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
              }}
              onClick={disabled ? undefined : () => insertSymbolAtCursor('✗ ')}
            >
              ✗
            </button>
          </div>

          <div style={{display: 'flex', justifyContent: 'flex-end', gap: '0.75rem'}}>
            <button
              type="button"
              disabled={disabled}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.5rem',
                border: '1px solid #e5e7eb',
                color: disabled ? '#9ca3af' : '#4b5563',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: disabled ? 'not-allowed' : 'pointer',
                backgroundColor: disabled ? '#f5f5f5' : 'transparent',
                opacity: disabled ? 0.5 : 1
              }}
              onClick={disabled ? undefined : handleCancel}
            >
              Cancel
            </button>
            <button
              disabled={disabled}
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '0.5rem',
                background: disabled
                  ? '#d1d5db'
                  : 'linear-gradient(to bottom right, #e4b17a, #c99b69)',
                color: disabled ? '#9ca3af' : 'white',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1
              }}
              onClick={disabled ? undefined : handleSave}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div
          style={{
            color: disabled ? '#9ca3af' : '#4b5563',
            lineHeight: '1.5',
            padding: '0.5rem',
            backgroundColor: disabled ? '#f0f0f0' : '#fafafa',
            borderRadius: '0.5rem',
            border: '1px solid #f0f0f0',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1
          }}
          onClick={disabled ? undefined : handleEdit}
          title={disabled ? "Editing disabled during AI learning" : "Click to edit feedback"}
        >
          {(() => {
            let displayedFeedback = '';

            if (localFeedback) {
              displayedFeedback = localFeedback;
              return renderFeedback(displayedFeedback);
            }

            return <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No feedback added yet</p>;
          })()}
        </div>
      )}
    </div>
  );
};

export default FeedbackPanel;
